import * as THREE from 'three';

/**
 * PlanetInfoSystem - Handles planet hover/click detection, tooltips, and selection
 */
export class PlanetInfoSystem {
    constructor(renderer3D, camera, canvas, onPlanetSelected = null) {
        this.renderer3D = renderer3D;
        this.camera = camera;
        this.canvas = canvas;
        this.scene = renderer3D.getScene();
        this.onPlanetSelected = onPlanetSelected;

        // Raycasting for mouse interaction
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();

        // State management
        this.hoveredPlanet = null;
        this.selectedPlanet = null;
        this.isEnabled = true;

        // UI elements
        this.tooltip = null;
        this.planetInfoPanel = null;

        // Event handlers
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseClick = this.onMouseClick.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);

        // Animation properties
        this.animationDuration = 300; // ms

        this.init();
    }

    /**
     * Initialize the planet info system
     */
    init() {
        this.createTooltip();
        this.createPlanetInfoPanel();
        this.setupEventListeners();
        console.log('Planet Info System initialized');
    }

    /**
     * Create tooltip element
     */
    createTooltip() {
        this.tooltip = document.createElement('div');
        this.tooltip.id = 'planet-tooltip';
        this.tooltip.style.cssText = `
            position: absolute;
            background: rgba(0, 0, 0, 0.9);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 14px;
            pointer-events: none;
            z-index: 1000;
            opacity: 0;
            transform: translateY(10px);
            transition: opacity ${this.animationDuration}ms ease, transform ${this.animationDuration}ms ease;
            border: 1px solid #444;
            backdrop-filter: blur(5px);
            max-width: 200px;
            line-height: 1.4;
        `;
        document.body.appendChild(this.tooltip);
    }

    /**
     * Create planet information panel
     */
    createPlanetInfoPanel() {
        const existingPanel = document.getElementById('planet-info');
        if (existingPanel) {
            this.planetInfoPanel = existingPanel;
        } else {
            this.planetInfoPanel = document.createElement('div');
            this.planetInfoPanel.id = 'planet-info';
            document.getElementById('ui-overlay').appendChild(this.planetInfoPanel);
        }

        this.planetInfoPanel.style.cssText = `
            margin-top: 20px;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid #333;
            border-radius: 8px;
            padding: 15px;
            opacity: 0;
            transform: translateY(20px);
            transition: opacity ${this.animationDuration}ms ease, transform ${this.animationDuration}ms ease;
            max-height: 0;
            overflow: hidden;
        `;

        this.planetInfoPanel.innerHTML = `
            <h4 style="margin: 0 0 10px 0; color: #fff; font-size: 16px;">
                Información del Planeta
            </h4>
            <div id="planet-details">
                <p style="color: #999; font-style: italic;">
                    Pasa el cursor sobre un planeta para ver su información
                </p>
            </div>
        `;
    }

    /**
     * Setup event listeners for mouse interaction
     */
    setupEventListeners() {
        this.canvas.addEventListener('mousemove', this.onMouseMove);
        this.canvas.addEventListener('click', this.onMouseClick);
        this.canvas.addEventListener('mouseleave', this.onMouseLeave);

        // Handle window resize
        window.addEventListener('resize', () => {
            this.hideTooltip();
        });
    }

    /**
     * Handle mouse move events
     * @param {MouseEvent} event - Mouse event
     */
    onMouseMove(event) {
        if (!this.isEnabled) return;

        this.updateMousePosition(event);
        const intersectedPlanet = this.getIntersectedPlanet();

        if (intersectedPlanet !== this.hoveredPlanet) {
            if (this.hoveredPlanet) {
                this.onPlanetHoverEnd(this.hoveredPlanet);
            }

            this.hoveredPlanet = intersectedPlanet;

            if (this.hoveredPlanet) {
                this.onPlanetHoverStart(this.hoveredPlanet);
            } else {
                this.hideTooltip();
            }
        }

        if (this.hoveredPlanet) {
            this.updateTooltipPosition(event);
        }
    }

    /**
     * Handle mouse click events
     * @param {MouseEvent} event - Mouse event
     */
    onMouseClick(event) {
        if (!this.isEnabled) return;

        this.updateMousePosition(event);
        const intersectedPlanet = this.getIntersectedPlanet();

        if (intersectedPlanet) {
            this.selectPlanet(intersectedPlanet);
        } else {
            this.deselectPlanet();
        }
    }

    /**
     * Handle mouse leave events
     */
    onMouseLeave() {
        if (this.hoveredPlanet) {
            this.onPlanetHoverEnd(this.hoveredPlanet);
            this.hoveredPlanet = null;
        }
        this.hideTooltip();
    }

    /**
     * Update mouse position for raycasting
     * @param {MouseEvent} event - Mouse event
     */
    updateMousePosition(event) {
        const rect = this.canvas.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    /**
     * Get intersected planet using raycasting
     * @returns {THREE.Object3D|null} Intersected planet mesh or null
     */
    getIntersectedPlanet() {
        this.raycaster.setFromCamera(this.mouse, this.camera);

        // Get all planet meshes and the sun
        const planets = this.renderer3D.planets;
        const sun = this.renderer3D.sun;
        const objects = [];

        if (sun) objects.push(sun);
        if (planets) {
            planets.forEach((planet) => objects.push(planet.mesh));
        }

        const intersects = this.raycaster.intersectObjects(objects);

        if (intersects.length > 0) {
            return intersects[0].object;
        }

        return null;
    }

    /**
     * Handle planet hover start
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    onPlanetHoverStart(planetMesh) {
        // Add hover effect to planet
        this.addHoverEffect(planetMesh);

        // Show tooltip
        this.showTooltip(planetMesh);

        // Change cursor
        this.canvas.style.cursor = 'pointer';
    }

    /**
     * Handle planet hover end
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    onPlanetHoverEnd(planetMesh) {
        // Remove hover effect if not selected
        if (planetMesh !== this.selectedPlanet) {
            this.removeHoverEffect(planetMesh);
        }

        // Reset cursor
        this.canvas.style.cursor = 'default';
    }

    /**
     * Add visual hover effect to planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    addHoverEffect(planetMesh) {
        // Store original scale if not already stored
        if (!planetMesh.userData.originalScale) {
            planetMesh.userData.originalScale = planetMesh.scale.clone();
        }

        // Animate scale increase
        this.animateScale(
            planetMesh,
            planetMesh.userData.originalScale.clone().multiplyScalar(1.2)
        );

        // Add glow effect if not selected
        if (planetMesh !== this.selectedPlanet) {
            this.addGlowEffect(planetMesh, 0x44aaff, 0.3);
        }
    }

    /**
     * Remove visual hover effect from planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    removeHoverEffect(planetMesh) {
        if (planetMesh.userData.originalScale) {
            // Animate scale back to original
            this.animateScale(planetMesh, planetMesh.userData.originalScale.clone());
        }

        // Remove glow effect
        this.removeGlowEffect(planetMesh);
    }

    /**
     * Add glow effect to planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     * @param {number} color - Glow color
     * @param {number} intensity - Glow intensity
     */
    addGlowEffect(planetMesh, color = 0x44aaff, intensity = 0.3) {
        if (planetMesh.material && !planetMesh.userData.originalEmissive) {
            // Store original emissive properties
            planetMesh.userData.originalEmissive = {
                emissive: planetMesh.material.emissive
                    ? planetMesh.material.emissive.clone()
                    : new THREE.Color(0x000000),
                emissiveIntensity: planetMesh.material.emissiveIntensity || 0,
            };

            // Apply glow effect
            if (planetMesh.material.emissive) {
                planetMesh.material.emissive.setHex(color);
                planetMesh.material.emissiveIntensity = intensity;
            }
        }
    }

    /**
     * Remove glow effect from planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    removeGlowEffect(planetMesh) {
        if (planetMesh.material && planetMesh.userData.originalEmissive) {
            // Restore original emissive properties
            if (planetMesh.material.emissive) {
                planetMesh.material.emissive.copy(planetMesh.userData.originalEmissive.emissive);
                planetMesh.material.emissiveIntensity =
                    planetMesh.userData.originalEmissive.emissiveIntensity;
            }
        }
    }

    /**
     * Animate planet scale
     * @param {THREE.Object3D} planetMesh - Planet mesh
     * @param {THREE.Vector3} targetScale - Target scale
     */
    animateScale(planetMesh, targetScale) {
        const startScale = planetMesh.scale.clone();
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / this.animationDuration, 1);

            // Smooth easing function
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            planetMesh.scale.lerpVectors(startScale, targetScale, easeProgress);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Show tooltip with planet information
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    showTooltip(planetMesh) {
        const planetData = this.getPlanetData(planetMesh);
        if (!planetData) return;

        const distance = this.formatDistance(planetData.orbitalRadius || 0);
        const period = this.formatPeriod(planetData.orbitalPeriod || 0);
        //const diameter = this.formatDiameter(planetData.radius || 0);
        const diameter = this.formatDiameter(planetData.radius || 0);
        const surfaceGravity = planetData.surfaceGravity;
        const mass = planetData.mass;

        this.tooltip.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 4px;">${planetData.name}</div>
            <div style="font-size: 12px; color: #ccc;">
                Distancia: ${distance}<br>
                Período orbital: ${period}<br>
                Diámetro: ${diameter}<br>
                Gravedad: ${surfaceGravity} m/s²<br>
                Masa: ${mass} Kg
            </div>
        `;

        this.tooltip.style.opacity = '1';
        this.tooltip.style.transform = 'translateY(0)';
    }

    /**
     * Hide tooltip
     */
    hideTooltip() {
        this.tooltip.style.opacity = '0';
        this.tooltip.style.transform = 'translateY(10px)';
    }

    /**
     * Update tooltip position
     * @param {MouseEvent} event - Mouse event
     */
    updateTooltipPosition(event) {
        const offset = 15;
        let x = event.clientX + offset;
        let y = event.clientY - offset;

        // Keep tooltip within viewport
        const tooltipRect = this.tooltip.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        if (x + tooltipRect.width > viewportWidth) {
            x = event.clientX - tooltipRect.width - offset;
        }

        if (y < 0) {
            y = event.clientY + offset;
        }

        this.tooltip.style.left = `${x}px`;
        this.tooltip.style.top = `${y}px`;
    }

    /**
     * Select a planet
     * @param {THREE.Object3D} planetMesh - Planet mesh to select
     */
    selectPlanet(planetMesh) {
        // Deselect previous planet
        if (this.selectedPlanet && this.selectedPlanet !== planetMesh) {
            this.deselectPlanet();
        }

        this.selectedPlanet = planetMesh;

        // Add selection effect
        this.addSelectionEffect(planetMesh);

        // Update info panel
        this.updateInfoPanel(planetMesh);

        // Start tracking the selected planet (Requirement 7.2)
        const planetData = this.getPlanetData(planetMesh);
        if (planetData && planetData.id) {
            // Only start camera tracking if the method exists (for real renderer)
            if (this.renderer3D && typeof this.renderer3D.startCameraTracking === 'function') {
                this.renderer3D.startCameraTracking(planetData.id);
            }

            // Notify callback if provided
            if (this.onPlanetSelected) {
                this.onPlanetSelected(planetData.id, planetMesh);
            }
        }

        console.log(`Planet selected: ${planetMesh.name}`);
    }

    /**
     * Deselect current planet
     */
    deselectPlanet() {
        if (this.selectedPlanet) {
            this.removeSelectionEffect(this.selectedPlanet);
            this.selectedPlanet = null;
            this.hideInfoPanel();
        }
    }

    /**
     * Add selection effect to planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    addSelectionEffect(planetMesh) {
        // Guardar la escala original si aún no se guardó (un planeta puede seleccionarse
        // sin hover previo; antes addSelectionEffect asumía que ya existía y la dejaba sin definir).
        if (!planetMesh.userData.originalScale) {
            planetMesh.userData.originalScale = planetMesh.scale.clone();
        }

        // Add stronger glow for selection
        this.addGlowEffect(planetMesh, 0xffaa00, 0.5);

        // Keep hover scale
        this.animateScale(
            planetMesh,
            planetMesh.userData.originalScale.clone().multiplyScalar(1.3)
        );
    }

    /**
     * Remove selection effect from planet
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    removeSelectionEffect(planetMesh) {
        this.removeGlowEffect(planetMesh);

        if (planetMesh.userData.originalScale) {
            this.animateScale(planetMesh, planetMesh.userData.originalScale.clone());
        }
    }

    /**
     * Update info panel with planet details
     * @param {THREE.Object3D} planetMesh - Planet mesh
     */
    updateInfoPanel(planetMesh) {
        const planetData = this.getPlanetData(planetMesh);
        if (!planetData) return;

        const detailsContainer = document.getElementById('planet-details');
        if (!detailsContainer) return;

        detailsContainer.innerHTML = this.generatePlanetDetailsHTML(planetData);

        // Botón "Enfocar Cámara": listener vía addEventListener en lugar de onclick inline a
        // window.solarSimulator. Usa el renderer3D inyectado, sin depender del global.
        const focusBtn = detailsContainer.querySelector('.focus-camera-btn');
        if (focusBtn) {
            focusBtn.addEventListener('click', () => {
                if (this.renderer3D && typeof this.renderer3D.focusOnPlanet === 'function') {
                    this.renderer3D.focusOnPlanet(planetData.id);
                }
            });
        }

        // Show panel with animation
        this.planetInfoPanel.style.maxHeight = '500px';
        this.planetInfoPanel.style.opacity = '1';
        this.planetInfoPanel.style.transform = 'translateY(0)';
    }

    /**
     * Hide info panel
     */
    hideInfoPanel() {
        this.planetInfoPanel.style.opacity = '0';
        this.planetInfoPanel.style.transform = 'translateY(20px)';

        setTimeout(() => {
            this.planetInfoPanel.style.maxHeight = '0';
            const detailsContainer = document.getElementById('planet-details');
            if (detailsContainer) {
                detailsContainer.innerHTML = `
                    <p style="color: #999; font-style: italic;">
                        Pasa el cursor sobre un planeta para ver su información
                    </p>
                `;
            }
        }, this.animationDuration);
    }

    /**
     * Generate HTML for planet details
     * @param {Object} planetData - Planet data
     * @returns {string} HTML string
     */
    generatePlanetDetailsHTML(planetData) {
        const distance = this.formatDistance(planetData.orbitalRadius || 0);
        const period = this.formatPeriod(planetData.orbitalPeriod || 0);
        const radius = this.formatDistance(planetData.radius || 0);
        const mass = this.formatMass(planetData.mass || 0);
        const gravity = planetData.surfaceGravity ? `${planetData.surfaceGravity} m/s²` : 'N/A';
        const moons = planetData.moons || 0;
        const atmosphere = planetData.atmosphereComposition || 'N/A';

        return `
            <div style="color: #fff;">
                <h5 style="margin: 0 0 10px 0; color: #4CAF50; font-size: 18px;">
                    ${planetData.name}
                </h5>
                
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
                    <div>
                        <strong style="color: #ccc;">Radio:</strong><br>
                        <span style="color: #fff;">${radius}</span>
                    </div>
                    <div>
                        <strong style="color: #ccc;">Masa:</strong><br>
                        <span style="color: #fff;">${mass}</span>
                    </div>
                    <div>
                        <strong style="color: #ccc;">Distancia del Sol:</strong><br>
                        <span style="color: #fff;">${distance}</span>
                    </div>
                    <div>
                        <strong style="color: #ccc;">Período Orbital:</strong><br>
                        <span style="color: #fff;">${period}</span>
                    </div>
                    <div>
                        <strong style="color: #ccc;">Gravedad:</strong><br>
                        <span style="color: #fff;">${gravity}</span>
                    </div>
                    <div>
                        <strong style="color: #ccc;">Lunas:</strong><br>
                        <span style="color: #fff;">${moons}</span>
                    </div>
                </div>
                
                <div style="margin-bottom: 10px;">
                    <strong style="color: #ccc;">Atmósfera:</strong><br>
                    <span style="color: #fff; font-size: 13px;">${atmosphere}</span>
                </div>
                
                <button class="focus-camera-btn" data-planet-id="${planetData.id}" 
                        style="background: #4CAF50; color: white; border: none; padding: 6px 12px; 
                               border-radius: 4px; cursor: pointer; font-size: 12px; margin-top: 5px;">
                    Enfocar Cámara
                </button>
            </div>
        `;
    }

    /**
     * Get planet data from mesh
     * @param {THREE.Object3D} planetMesh - Planet mesh
     * @returns {Object|null} Planet data
     */
    getPlanetData(planetMesh) {
        if (!planetMesh || !planetMesh.userData) return null;

        // For sun
        if (planetMesh.userData.id === 'sun') {
            return {
                id: 'sun',
                name: 'Sol',
                radius: planetMesh.userData.realRadius || 696340,
                mass: planetMesh.userData.mass || 1.989e30,
                temperature: planetMesh.userData.temperature || 5778,
                orbitalRadius: 0,
                orbitalPeriod: 0,
                surfaceGravity: 274,
                moons: 0,
                atmosphereComposition: 'Plasma de hidrógeno y helio',
            };
        }

        // For planets, combine userData with additional info
        return {
            id: planetMesh.userData.id,
            name: planetMesh.name,
            radius: planetMesh.userData.realRadius,
            mass: planetMesh.userData.mass,
            orbitalRadius: planetMesh.userData.orbitalRadius,
            orbitalPeriod: planetMesh.userData.orbitalPeriod,
            rotationPeriod: planetMesh.userData.rotationPeriod,
            surfaceGravity: planetMesh.userData.surfaceGravity,
            moons: planetMesh.userData.moons,
            atmosphereComposition: planetMesh.userData.atmosphereComposition,
            hasRings: planetMesh.userData.hasRings,
        };
    }

    /**
     * Format distance for display
     * @param {number} distance - Distance in km
     * @returns {string} Formatted distance
     */
    formatDistance(distance) {
        if (distance === 0) return '0 km';
        if (distance < 10000) {
            return `${Math.round(distance).toLocaleString('en-US')} km`;
        } else if (distance < 1000000) {
            return `${Math.round(distance / 1000).toLocaleString('en-US')} mil km`;
        } else {
            return `${(distance / 1000000).toFixed(1)} millones km`;
        }
    }

    /**
     * Format orbital period for display
     * @param {number} period - Period in days
     * @returns {string} Formatted period
     */
    formatPeriod(period) {
        if (period === 0) return 'N/A';
        if (period < 365) {
            return `${Math.round(period)} días`;
        } else {
            const years = (period / 365.25).toFixed(1);
            return `${years} años`;
        }
    }

    /**
     * Format diameter for display
     * @param {number} radius - Period in days
     * @returns {string} Formatted diameter
     */
    formatDiameter(radius) {
        if (radius === 0) return 'N/A';
        return `${radius * 2} km`;
    }

    /**
     * Format mass for display
     * @param {number} mass - Mass in kg
     * @returns {string} Formatted mass
     */
    formatMass(mass) {
        if (mass === 0) return 'N/A';
        const earthMass = 5.972e24;
        const ratio = mass / earthMass;

        if (ratio < 0.1) {
            return `${(ratio * 100).toFixed(1)}% Tierra`;
        } else if (ratio < 10) {
            return `${ratio.toFixed(2)} × Tierra`;
        } else {
            return `${Math.round(ratio)} × Tierra`;
        }
    }

    /**
     * Enable the planet info system
     */
    enable() {
        this.isEnabled = true;
    }

    /**
     * Disable the planet info system
     */
    disable() {
        this.isEnabled = false;
        this.hideTooltip();
        this.deselectPlanet();
    }

    /**
     * Get currently selected planet
     * @returns {THREE.Object3D|null} Selected planet mesh
     */
    getSelectedPlanet() {
        return this.selectedPlanet;
    }

    /**
     * Get currently hovered planet
     * @returns {THREE.Object3D|null} Hovered planet mesh
     */
    getHoveredPlanet() {
        return this.hoveredPlanet;
    }

    /**
     * Cleanup resources
     */
    dispose() {
        // Remove event listeners
        this.canvas.removeEventListener('mousemove', this.onMouseMove);
        this.canvas.removeEventListener('click', this.onMouseClick);
        this.canvas.removeEventListener('mouseleave', this.onMouseLeave);

        // Remove tooltip
        if (this.tooltip && this.tooltip.parentNode) {
            this.tooltip.parentNode.removeChild(this.tooltip);
        }

        // Reset planet effects y limpiar referencias (evita fugas y deja el estado consistente)
        if (this.selectedPlanet) {
            this.removeSelectionEffect(this.selectedPlanet);
            this.selectedPlanet = null;
        }

        if (this.hoveredPlanet) {
            this.removeHoverEffect(this.hoveredPlanet);
            this.hoveredPlanet = null;
        }

        // Reset cursor
        this.canvas.style.cursor = 'default';

        console.log('Planet Info System disposed');
    }
}
